import { useMemo, useState } from 'react'
import { Background, Controls, Edge, MarkerType, MiniMap, Node, ReactFlow, ReactFlowProvider } from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import { Box, Chip, Typography } from '@mui/material'
import { CROSS_REF_RELATIONS, KU_TYPES, KnowledgeUnit, KuCrossRef, PASS_META, PassId } from '../../types'

interface Props {
  /** All KUs in the currently-selected standard. */
  kus: KnowledgeUnit[]
  /** Cross-refs that connect KUs in this standard. */
  refs: KuCrossRef[]
}

// ── Layout geometry ─────────────────────────────────────────────────────────
const NODE_WIDTH = 240
const NODE_VERTICAL_GAP = 14
const NODE_HEIGHT = 62
const COLUMN_X: Record<PassId, number> = {
  pass1: 0,
  pass2: NODE_WIDTH + 220,
  pass3: 2 * (NODE_WIDTH + 220),
}

const TYPE_COLOR_MAP = Object.fromEntries(KU_TYPES.map((t) => [t.value, t.color]))
const RELATION_META_MAP = Object.fromEntries(CROSS_REF_RELATIONS.map((m) => [m.value, m]))

/**
 * Force-simple graph of KUs and their relations for one standard.
 *   • Nodes are laid out in three columns by pass (REQ | CONT | KNOW).
 *   • Edges are colored and dashed per relation type.
 *   • Researcher can filter by relation type. Clicking a node dims
 *     everything not in its immediate neighborhood.
 *
 * Read-only view — all editing happens in the existing matrix UI.
 */
export default function RelationshipGraph({ kus, refs }: Props) {
  const [activeFilter, setActiveFilter] = useState<'all' | KuCrossRef['relation_type']>('all')
  const [focusNode, setFocusNode] = useState<string | null>(null)

  // Build nodes: group by pass, stack within each column.
  const nodes: Node[] = useMemo(() => {
    const byPass: Record<PassId, KnowledgeUnit[]> = { pass1: [], pass2: [], pass3: [] }
    for (const k of kus) byPass[k.pass_id]?.push(k)
    const result: Node[] = []
    for (const pass of PASS_META) {
      const bucket = byPass[pass.id]
      bucket.forEach((ku, i) => {
        const thisType = ku.type || ku.proposed_type || 'unclear'
        const typeColor = TYPE_COLOR_MAP[thisType] || '#64748b'
        result.push({
          id: ku.id,
          position: { x: COLUMN_X[ku.pass_id], y: i * (NODE_HEIGHT + NODE_VERTICAL_GAP) + 40 },
          data: {
            label: (
              <Box sx={{ textAlign: 'left', p: 0.5 }}>
                <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', mb: 0.25 }}>
                  <Chip
                    label={thisType}
                    size="small"
                    sx={{
                      height: 14,
                      fontSize: 9,
                      fontWeight: 700,
                      bgcolor: typeColor + '18',
                      color: typeColor,
                      border: `1px solid ${typeColor}40`,
                    }}
                  />
                  <Typography sx={{ fontSize: 9, color: pass.color, fontWeight: 700 }}>{pass.label}</Typography>
                </Box>
                <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'text.primary', lineHeight: 1.3 }}>
                  {ku.name}
                </Typography>
              </Box>
            ),
          },
          style: {
            width: NODE_WIDTH,
            border: `2px solid ${pass.color}40`,
            borderLeft: `4px solid ${typeColor}`,
            borderRadius: 6,
            background: 'white',
            padding: 4,
            fontSize: 11,
          },
          sourcePosition: 'right' as any, // eslint-disable-line
          targetPosition: 'left' as any, // eslint-disable-line
        })
      })
    }
    return result
  }, [kus])

  // Build edges, filtered per the active relation filter.
  // same_as edges are color-split by classification agreement: green when
  // both KUs carry the same type (true identity), amber when they diverge
  // (taxonomy-ambiguity signal — the pair is semantically the same but the
  // classifier assigned different CDT/ID2 types).
  const kuById = useMemo(() => new Map(kus.map((k) => [k.id, k])), [kus])
  const edges: Edge[] = useMemo(() => {
    const filtered = activeFilter === 'all' ? refs : refs.filter((r) => r.relation_type === activeFilter)
    return filtered.map((r) => {
      const meta = RELATION_META_MAP[r.relation_type]
      let color = meta?.color || '#64748b'
      let label = meta?.shortLabel || r.relation_type
      if (r.relation_type === 'same_as') {
        const a = kuById.get(r.from_ku_id)
        const b = kuById.get(r.to_ku_id)
        const labelA = a ? a.type || a.proposed_type : null
        const labelB = b ? b.type || b.proposed_type : null
        const classMismatch = !!(labelA && labelB && labelA !== labelB)
        if (classMismatch) {
          color = '#d97706' // amber — taxonomy-ambiguity signal
          label = `⚠ Same · ${labelA}≠${labelB}`
        }
      }
      const symmetric = !meta?.directional
      return {
        id: r.id,
        source: r.from_ku_id,
        target: r.to_ku_id,
        label,
        labelStyle: { fontSize: 10, fontWeight: 700, fill: color },
        labelBgStyle: { fill: '#fff', fillOpacity: 0.9 },
        labelBgPadding: [4, 2],
        labelBgBorderRadius: 3,
        style: {
          stroke: color,
          strokeWidth: 1.5,
          strokeDasharray: r.relation_type === 'related_to' ? '6 4' : undefined,
        },
        type: 'smoothstep',
        markerEnd: symmetric ? undefined : { type: MarkerType.ArrowClosed, color },
        markerStart: r.relation_type === 'same_as' ? { type: MarkerType.ArrowClosed, color } : undefined,
      }
    })
  }, [refs, activeFilter, kuById])

  // Dim non-focus nodes when a focus is set.
  const displayNodes = useMemo(() => {
    if (!focusNode) return nodes
    const connected = new Set<string>([focusNode])
    for (const e of edges) {
      if (e.source === focusNode) connected.add(e.target)
      if (e.target === focusNode) connected.add(e.source)
    }
    return nodes.map((n) => ({
      ...n,
      style: {
        ...n.style,
        opacity: connected.has(n.id) ? 1 : 0.2,
      },
    }))
  }, [nodes, edges, focusNode])

  const displayEdges = useMemo(() => {
    if (!focusNode) return edges
    return edges.map((e) => ({
      ...e,
      style: {
        ...e.style,
        opacity: e.source === focusNode || e.target === focusNode ? 1 : 0.1,
      },
    }))
  }, [edges, focusNode])

  return (
    <Box sx={{ height: 620, border: '1px solid', borderColor: 'divider', borderRadius: 1.5, bgcolor: '#fafbfc' }}>
      {/* Filter bar */}
      <Box
        sx={{
          px: 1.5,
          py: 1,
          borderBottom: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          flexWrap: 'wrap',
        }}
      >
        <Typography sx={{ fontSize: 10, fontWeight: 700, color: 'text.secondary', letterSpacing: '0.05em', mr: 1 }}>
          FILTER
        </Typography>
        <Chip
          label={`All (${refs.length})`}
          size="small"
          onClick={() => setActiveFilter('all')}
          sx={{
            fontSize: 10,
            fontWeight: 700,
            bgcolor: activeFilter === 'all' ? 'primary.main' : 'grey.100',
            color: activeFilter === 'all' ? 'white' : 'text.primary',
            cursor: 'pointer',
          }}
        />
        {CROSS_REF_RELATIONS.map((m) => {
          const count = refs.filter((r) => r.relation_type === m.value).length
          if (count === 0) return null
          const active = activeFilter === m.value
          return (
            <Chip
              key={m.value}
              label={`${m.label} (${count})`}
              size="small"
              onClick={() => setActiveFilter(m.value)}
              sx={{
                fontSize: 10,
                fontWeight: 700,
                bgcolor: active ? m.color : m.color + '18',
                color: active ? 'white' : m.color,
                border: `1px solid ${m.color}40`,
                cursor: 'pointer',
              }}
            />
          )
        })}
        {focusNode && (
          <Chip
            label="✕ Clear focus"
            size="small"
            onClick={() => setFocusNode(null)}
            sx={{ fontSize: 10, fontWeight: 700, bgcolor: '#e2e8f0', cursor: 'pointer', ml: 'auto' }}
          />
        )}
      </Box>

      {/* Graph canvas */}
      <Box sx={{ width: '100%', height: 'calc(100% - 45px)' }}>
        <ReactFlowProvider>
          <ReactFlow
            nodes={displayNodes}
            edges={displayEdges}
            onNodeClick={(_, n) => setFocusNode(n.id === focusNode ? null : n.id)}
            fitView
            nodesDraggable
            nodesConnectable={false}
            edgesFocusable={false}
            proOptions={{ hideAttribution: true }}
          >
            <Background gap={16} />
            <Controls showInteractive={false} />
            <MiniMap pannable nodeColor={(n) => (n.style?.borderLeft as string | undefined)?.split(' ')[2] || '#888'} />
          </ReactFlow>
        </ReactFlowProvider>
      </Box>
    </Box>
  )
}
